import React, { useContext } from "react";
import type { backendReportStatisticalResponseDTO } from "../../backend/target/typescript/dto";
import { AuthContext } from "../auth-context";
import { RequestContext } from "../request-context";
import { requestGet, requireResult } from "../utils/network";
import { toTitleCase } from "../utils/strings";
import { IPeriod } from "../widget/i-period";
import { Modes } from "../widget/io-abstract";
import IOList from "../widget/io-list";
import SearchPanel, { getPref, type FormValues } from "../widget/search-panel";
import TablePanel from "../widget/table-panel";

type ServerResponse = backendReportStatisticalResponseDTO;

const maxDataColumns = 4;
function header(label: string, ...labels: string[]): React.ReactNode {
  const extra = Math.max(maxDataColumns - labels.length, 0);
  return (
    <>
      <th>{label}</th>
      {labels.map((label, index) => (
        <th key={index}>{label}</th>
      ))}
      {extra > 0 && <th colSpan={extra}></th>}
    </>
  );
}

function row(label: string, ...values: (string | number)[]): React.ReactNode {
  const extra = Math.max(maxDataColumns - values.length, 0);
  return (
    <>
      <td>{label}</td>
      {values.map((value, index) => (
        <td key={index}>{value}</td>
      ))}
      {extra > 0 && <td colSpan={extra}></td>}
    </>
  );
}

function round(nbr: number, precision: number = 0): number {
  return Math.round(nbr * Math.pow(10, precision)) / Math.pow(10, precision);
}

function ratio(numerator: number, denominator: number): number {
  return denominator === 0 ? 0 : numerator / denominator;
}

function tk(value: number | null | undefined): string {
  if (value) {
    return `${round(value, 0)} ৳`;
  }
  return "-";
}

export default function ReportStatistical(): React.ReactNode {
  const authenticationContext = useContext(AuthContext)!;
  const requestContext = useContext(RequestContext)!;

  const onSearch = (formData: FormValues): Promise<React.ReactNode> =>
    requestContext
      .fetchJSONAndMessage<ServerResponse>(
        "Generating report",
        requestGet("/api/reports/statistical", formData)
      )
      .then(requireResult)
      .then((reportData: ServerResponse) => {
        return [
          header("Requested"),
          row(
            "Period",
            formData["when"] ||
              (formData["period"] === "day"
                ? formData["day"]
                : formData["period"] === "month"
                  ? formData["month"]
                  : formData["year"])
          ),

          /*
           * Patients seen
           */

          header("Patients seen", "count"),
          row("Number of patients seen", reportData.nb_patients),

          /*
           * Pathology
           */
          header("Patients per Pathology", "count"),
          row(
            "Ricket consults",
            reportData.pathologies.counts["Ricket"]?.total ?? 0
          ),
          row(
            "Ricket consults (new only)",
            reportData.pathologies.counts["Ricket"]?.new_one ?? 0
          ),
          row(
            "Ricket consults (old only)",
            (reportData.pathologies.counts["Ricket"]?.total ?? 0) -
              (reportData.pathologies.counts["Ricket"]?.new_one ?? 0)
          ),
          row(
            "Club Foots",
            reportData.pathologies.counts["ClubFoot"]?.total ?? 0
          ),
          row(
            "Club Foots (new only)",
            reportData.pathologies.counts["ClubFoot"]?.new_one ?? 0
          ),
          row(
            "Club Foots (old only)",
            (reportData.pathologies.counts["ClubFoot"]?.total ?? 0) -
              (reportData.pathologies.counts["ClubFoot"]?.new_one ?? 0)
          ),

          ...Object.entries(reportData.pathologies.counts)
            .filter(([key, val]) => key !== "Ricket" && key !== "ClubFoot")
            .map(([key, val]) => row(toTitleCase(key), val.total)),

          row("All consultations", reportData.pathologies.total),

          /*
           * Social Levels
           */
          header("Social Level", "count", "real", "asked", "paid"),
          row(
            "Family income (mean)",
            tk(reportData.social_level.family_income)
          ),
          row(
            "Nb household mb (mean)",
            round(reportData.social_level.nb_household ?? 0, 1)
          ),
          row(
            "ratio (mean)",
            tk(
              ratio(
                reportData.social_level.family_income ?? 0,
                reportData.social_level.nb_household ?? 0
              )
            )
          ),
          ...Array(6)
            .keys()
            .map((level) =>
              row(
                `Social Level ${level}`,
                reportData.social_level.per_social_levels[level]?.count ?? 0,
                tk(reportData.social_level.per_social_levels[level]?.price),
                tk(reportData.social_level.per_social_levels[level]?.asked),
                tk(reportData.social_level.per_social_levels[level]?.paid)
              )
            ),
          row("All social level together", reportData.social_level.total),

          /*
           * Centers
           */
          header("Centers", "count", "real", "asked", "paid"),
          ...Object.keys(reportData.centers).map((center) =>
            row(
              `@${center ?? "unspecified"}`,
              reportData.centers[center].count ?? 0,
              tk(reportData.centers[center].price),
              tk(reportData.centers[center].asked),
              tk(reportData.centers[center].paid)
            )
          ),

          /*
           * Activities
           */
          header("Activity", "count", "average order"),
          ...Object.entries(reportData.activities).flatMap(([name, data]) => [
            header(`Activity → ${name}`, "count", "average price"),
            ...Object.entries(data)
              .sort()
              .map(([key, value]) =>
                row(key, value.count ?? 0, tk(value.average))
              )
          ])
        ];
      })
      .then((rows) => (
        <TablePanel
          rows={rows}
          headers={
            <thead>
              <tr>
                <th>Info</th>
                <th colSpan={maxDataColumns}>Values</th>
              </tr>
            </thead>
          }

          reportBasename={`statistical ${formData["period"] == "day" ? formData["day"] : formData["period"] == "month" ? formData["month"] : formData["year"]}`}
        />
      ));

  return (
    <div>
      <h1>
        <img src="/static/img/reports.gif" />
        Statistical Report
      </h1>

      <SearchPanel
        form={
          <>
            <IPeriod />
            <IOList
              name="center"
              mode={Modes.input}
              list={authenticationContext.getList("Centers")}
              value={getPref("center", "")}
            />
            <IOList
              name="examiner"
              mode={Modes.input}
              list={authenticationContext.getList("Examiners")}
              value={getPref("examiner", "")}
            />
          </>
        }
        onSearch={onSearch}
      />
    </div>
  );
}
